import React, { useEffect, useState } from "react";
import {
  Table,
  Spin,
  Tag,
  Tabs,
  Collapse,
  Typography,
  Badge,
  Statistic,
  Row,
  Col,
  Card,
  Tooltip,
  Input,
} from "antd";
import {
  ShopOutlined,
  ToolOutlined,
  TeamOutlined,
  InboxOutlined,
  UserOutlined,
  CalendarOutlined,
  ExperimentOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { inventory } from "../api";
import PageHeader from "../components/common/PageHeader";

const { Text } = Typography;

function fmtQty(v) {
  const n = Number(v ?? 0);
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(3);
}

function fmtCurrency(v) {
  return (
    "₹" +
    Number(v ?? 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function stockColor(n, threshold = 2) {
  if (n <= 0) return "var(--danger, #f5222d)";
  if (threshold > 0 && n <= threshold) return "var(--warning, #fa8c16)";
  return "var(--success, #52c41a)";
}

/**
 * FIX: Display holding time as "X days Y hrs" from minutesOut.
 * Previously only showed integer days, losing intra-day precision.
 */
function fmtHoldTime(minutesOut, daysOut) {
  if (!minutesOut && minutesOut !== 0) {
    // fallback for old records that only have daysOut
    return `${daysOut} day${daysOut !== 1 ? "s" : ""}`;
  }
  const totalMins = Number(minutesOut);
  const d = Math.floor(totalMins / (60 * 24));
  const h = Math.floor((totalMins % (60 * 24)) / 60);
  const m = totalMins % 60;
  if (d > 0 && h > 0) return `${d}d ${h}h`;
  if (d > 0) return `${d}d`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/* ───────── Sale Products Table ───────── */
function SaleInventory({ items }) {
  const cols = [
    {
      title: "Product",
      dataIndex: "productName",
      render: (v) => <span className="mono">{v}</span>,
    },
    { title: "Unit", dataIndex: "unit", render: (v) => <Tag>{v}</Tag> },
    {
      title: "Available Qty",
      dataIndex: "stock",
      render: (v, row) => {
        const n = Number(v);
        const color = stockColor(n, row.lowStockThreshold);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="mono" style={{ color, minWidth: 60 }}>
              {fmtQty(n)}
            </span>
            <div
              style={{
                flex: 1,
                height: 4,
                background: "var(--surface2,#f0f0f0)",
                borderRadius: 2,
                minWidth: 80,
              }}
            >
              <div
                style={{
                  height: "100%",
                  background: color,
                  borderRadius: 2,
                  width: `${Math.min(100, Math.max(0, (n / 20) * 100))}%`,
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "stock",
      render: (v, row) => {
        const n = Number(v);
        const thr = row.lowStockThreshold || 0;
        if (n <= 0) return <Tag color="red">Out of Stock</Tag>;
        if (thr > 0 && n <= thr) return <Tag color="orange">Low Stock</Tag>;
        return <Tag color="green">In Stock</Tag>;
      },
    },
  ];

  return (
    <Table
      columns={cols}
      dataSource={items}
      rowKey="productId"
      scroll={{ x: "max-content" }}
      size="small"
      pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10","20","50"], size: "small" }}
      summary={(rows) => (
        <Table.Summary.Row style={{ color: "gray" }}>
          <Table.Summary.Cell colSpan={2} index={0}>
            <strong className="mono">Total: {rows.length} products</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell colSpan={2} index={2}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Stock = Purchases + SaleReturns(WH) − Sales − Currently Rented −
              Scrapped
            </Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
}

/* ───────── Rental: Customer Detail Table ───────── */
function CustomerTable({ customers, unit }) {
  const cols = [
    {
      title: "Customer",
      dataIndex: "customerName",
      render: (v, r) => (
        <span>
          <UserOutlined style={{ marginRight: 6 }} />
          {v}
          {r.phone && (
            <Text
              type="secondary"
              style={{ marginLeft: 8, fontSize: 12, color: "gray" }}
            >
              ({r.phone})
            </Text>
          )}
        </span>
      ),
    },
    {
      title: "Rental No",
      dataIndex: "rentalNo",
      render: (v) => <Tag color="purple">{v}</Tag>,
    },
    {
      title: "Issued",
      dataIndex: "rentalDateTime",
      render: (v, r) => (
        <Tooltip title={v ? `Exact: ${v}` : "Time not recorded"}>
          <span>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {r.rentalDate}
          </span>
        </Tooltip>
      ),
    },
    {
      title: `Qty Out (${unit})`,
      dataIndex: "quantityOut",
      render: (v) => (
        <span
          className="mono"
          style={{ color: "var(--warning,#fa8c16)", fontWeight: 600 }}
        >
          {fmtQty(v)}
        </span>
      ),
    },
    {
      // FIX: show holding time as "Xd Yh" using minutesOut for precision
      title: "Holding Time",
      render: (_, r) => {
        const mins = r.minutesOut;
        const days = r.daysOut;
        const label = fmtHoldTime(mins, days);
        const color = days > 30 ? "red" : days > 14 ? "orange" : "blue";
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      // FIX: billSoFar now includes tax + rate-change boundaries (computed by RentCalculator)
      title: "Bill So Far (incl. tax)",
      dataIndex: "billSoFar",
      render: (v) => (
        <Tooltip title="Tax-inclusive estimate using current rates">
          <span
            className="mono"
            style={{ fontWeight: 600, color: "var(--accent,#1677ff)" }}
          >
            {fmtCurrency(v)}
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Table
      columns={cols}
      scroll={{ x: "max-content" }}
      dataSource={customers}
      rowKey={(r) => r.rentalNo + r.customerId}
      size="small"
      pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
    />
  );
}

/* ───────── Rental Inventory Panel ───────── */
function RentalInventoryPanel({ items }) {
  if (!items || items.length === 0) {
    return (
      <div style={{ padding: 24, color: "var(--text-muted,#888)" }}>
        No rental products found.
      </div>
    );
  }

  const collapseItems = items.map((p) => {
    const hasInService = Number(p.inService ?? 0) > 0;

    return {
      key: String(p.productId),
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <span
            className="mono"
            style={{ fontWeight: 600, minWidth: 160, color: "gray" }}
          >
            {p.productName}
          </span>
          <Tag>{p.unit}</Tag>
          <Tag color={p.productType === "BOTH" ? "green" : "purple"}>
            {p.productType}
          </Tag>

          <span
            style={{
              display: "flex",
              gap: 20,
              marginLeft: "auto",
              flexWrap: "wrap",
            }}
          >
            <Tooltip title="Total owned (purchased + sale returns − scrapped)">
              <span style={{ fontSize: 13, color: "var(--success,#52c41a)" }}>
                <InboxOutlined style={{ marginRight: 4 }} />
                Total: <strong>{fmtQty(p.totalQuantity)}</strong>
              </span>
            </Tooltip>
            <Tooltip title="Sitting in warehouse, ready to rent">
              <span style={{ fontSize: 13, color: "var(--success,#52c41a)" }}>
                <ShopOutlined style={{ marginRight: 4 }} />
                Storage: <strong>{fmtQty(p.inStorage)}</strong>
              </span>
            </Tooltip>
            <Tooltip title="Currently out with customers">
              <span style={{ fontSize: 13, color: "var(--warning,#fa8c16)" }}>
                <TeamOutlined style={{ marginRight: 4 }} />
                With Customers: <strong>{fmtQty(p.withCustomers)}</strong>
              </span>
            </Tooltip>
            {/* FIX: show In Service count in the header if non-zero */}
            {hasInService && (
              <Tooltip title="At service center for repair — unavailable">
                <span style={{ fontSize: 13, color: "var(--danger,#f5222d)" }}>
                  <ExperimentOutlined style={{ marginRight: 4 }} />
                  In Service: <strong>{fmtQty(p.inService)}</strong>
                </span>
              </Tooltip>
            )}
          </span>
        </div>
      ),
      children: (
        <div>
          {/* Summary cards */}
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={hasInService ? 6 : 8}>
              <Card size="small" style={{ textAlign: "center" }}>
                <Statistic
                  title="Total Quantity"
                  value={fmtQty(p.totalQuantity)}
                  suffix={p.unit}
                  valueStyle={{ fontSize: 18, color: "#fa8c16" }}
                  prefix={<InboxOutlined />}
                />
                <Text type="secondary" style={{ fontSize: 11, color: "gray" }}>
                  Purchased + Returns − Scrapped
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={hasInService ? 6 : 8}>
              <Card size="small" style={{ textAlign: "center" }}>
                <Statistic
                  title="In Storage"
                  value={fmtQty(p.inStorage)}
                  suffix={p.unit}
                  valueStyle={{ fontSize: 18, color: "#52c41a" }}
                  prefix={<ShopOutlined />}
                />
                {/* FIX: clarify the deduction breakdown */}
                <Text type="secondary" style={{ fontSize: 11, color: "gray" }}>
                  Available = Total − With Customers
                  {hasInService ? " − In Service" : ""}
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={hasInService ? 6 : 8}>
              <Card size="small" style={{ textAlign: "center" }}>
                <Statistic
                  title="With Customers"
                  value={fmtQty(p.withCustomers)}
                  suffix={p.unit}
                  valueStyle={{ fontSize: 18, color: "#fa8c16" }}
                  prefix={<TeamOutlined />}
                />
                <Text type="secondary" style={{ fontSize: 11, color: "gray" }}>
                  {p.customerDetails?.length || 0} active rental(s)
                </Text>
              </Card>
            </Col>

            {/* FIX: show In Service card only when > 0 */}
            {hasInService && (
              <Col xs={24} sm={6}>
                <Card
                  size="small"
                  style={{ textAlign: "center", borderColor: "#f5222d" }}
                >
                  <Statistic
                    title="In Service"
                    value={fmtQty(p.inService)}
                    suffix={p.unit}
                    valueStyle={{ fontSize: 18, color: "#f5222d" }}
                    prefix={<ExperimentOutlined />}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    At repair shop — unavailable
                  </Text>
                </Card>
              </Col>
            )}
          </Row>

          {/* Customer detail table */}
          {p.customerDetails && p.customerDetails.length > 0 ? (
            <>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                <UserOutlined style={{ marginRight: 6 }} />
                Customers currently holding this product:
              </Text>
              <CustomerTable customers={p.customerDetails} unit={p.unit} />
            </>
          ) : (
            <Tag color="green">All items accounted for — no active rentals</Tag>
          )}
        </div>
      ),
    };
  });

  return (
    <Collapse
      items={collapseItems}
      size="small"
      defaultActiveKey={items.map((p) => String(p.productId))}
    />
  );
}

/* ───────── Main Inventory Page ───────── */
export default function Inventory() {
  const [saleProducts, setSaleProducts] = useState([]);
  const [rentalProducts, setRentalProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    inventory
      .stock()
      .then((r) => {
        const d = r.data.data;
        setSaleProducts(d.saleProducts || []);
        setRentalProducts(d.rentalProducts || []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const filteredSale   = q ? saleProducts.filter(p => p.productName?.toLowerCase().includes(q.toLowerCase())) : saleProducts;
  const filteredRental = q ? rentalProducts.filter(p => p.productName?.toLowerCase().includes(q.toLowerCase())) : rentalProducts;

  const tabItems = [
    {
      key: "sale",
      label: (
        <span>
          <ShopOutlined />
          Sale Products
          <Badge
            count={filteredSale.length}
            style={{ marginLeft: 8, backgroundColor: "#1677ff" }}
          />
        </span>
      ),
      children: <SaleInventory items={filteredSale} />,
    },
    {
      key: "rental",
      label: (
        <span>
          <ToolOutlined />
          Rental Products
          <Badge
            count={filteredRental.length}
            style={{ marginLeft: 8, backgroundColor: "#722ed1" }}
          />
        </span>
      ),
      children: <RentalInventoryPanel items={filteredRental} />,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Inventory"
        subtitle="Sale stock levels and rental item tracking"
      />
      <Input
        prefix={<SearchOutlined />}
        placeholder="Search product name..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        allowClear
        style={{ width: 260, marginBottom: 16 }}
      />
      <Tabs defaultActiveKey="sale" items={tabItems} />
    </div>
  );
}